import { useEffect, useRef, useState } from 'react';
import { cloudRenderClient } from '../services/cloud-video';
import { localCapabilities, listVideos, createVideo, cancelVideo, type VideoJob, type VideoRequest } from '../services/local-video';
import { videoFetch } from '../services/video-http';
import { API, apiRequest, jsonPost } from '../services/api-client';
import { useCommerce, type Entitlements } from '../account/commerce';

export type RenderDestination = 'local' | 'remote';
export interface RemoteConnection { available: boolean; connected: boolean; connectConfigured?: boolean; compatible?: boolean; message: string; accountId?: string; entitlements?: Entitlements }
const PREFIX = '/api/local/remote';
const remote = cloudRenderClient(PREFIX);
function remembered(): RenderDestination { try { const requested = new URLSearchParams(location.search).get('destination'); if (requested === 'local' || requested === 'remote') return requested; return localStorage.getItem('render-destination') === 'remote' ? 'remote' : 'local'; } catch { return 'local'; } }
const tag = (jobs: VideoJob[], destination: RenderDestination) => jobs.map(job => ({ ...job, destination,
  ...(destination === 'remote' && job.downloadUrl?.startsWith('/api/') ? { downloadUrl: PREFIX + job.downloadUrl } : {}),
  ...(destination === 'remote' && job.thumbnailUrl?.startsWith('/api/') ? { thumbnailUrl: PREFIX + job.thumbnailUrl } : {}),
}));

/** Both histories remain attached to their original service while the next export destination changes. */
export function useLocalRenderDestinations(enabled: boolean, filter = '') {
  const baseCommerce = useCommerce();
  const [active, setActive] = useState(false);
  const [destination, choose] = useState<RenderDestination>(remembered);
  const [connection, setConnection] = useState<RemoteConnection>({ available: false, connected: false, message: 'Checking production connection…' });
  const [localJobs, setLocalJobs] = useState<VideoJob[]>([]);
  const [remoteJobs, setRemoteJobs] = useState<VideoJob[]>([]);
  const [localAvailable, setLocalAvailable] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const current = useRef(destination); current.current = destination;
  useEffect(() => {
    if (!enabled) return;
    let alive = true; let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      const [local, status] = await Promise.allSettled([
        listVideos(), videoFetch(PREFIX + '/status').then(response => response.json() as Promise<RemoteConnection>),
      ]);
      if (!alive) return;
      setLocalAvailable(local.status === 'fulfilled');
      if (local.status === 'fulfilled') setLocalJobs(tag(local.value, 'local'));
      const state = status.status === 'fulfilled' ? status.value : { available: false, connected: false, message: 'Production connection unavailable.' };
      setConnection(state);
      if (state.connected) {
        try { const page = await remote.cloudVideos(); if (alive) {
          setRemoteJobs(previous => [...tag(page.jobs, 'remote'), ...previous.filter(job => !page.jobs.some(fresh => fresh.id === job.id))]);
          setNextCursor(page.nextCursor);
        } } catch { if (alive) setConnection({ ...state, available: false, message: 'Unable to refresh production jobs. Existing local jobs remain available.' }); }
      }
      if (alive) timer = setTimeout(() => void poll(), 5000);
    };
    const connect = async () => {
      try {
        const capabilities = await localCapabilities();
        if (!alive || !capabilities.renderDestinations) return;
        setActive(true); void poll();
      } catch {
        if (alive) timer = setTimeout(() => void connect(), 3000);
      }
    };
    void connect();
    return () => { alive = false; clearTimeout(timer); };
  }, [enabled]);
  const setDestination = (value: RenderDestination) => {
    choose(value); setError(''); try { localStorage.setItem('render-destination', value); } catch { /* Selection still works without persistence. */ }
  };
  const update = (job: VideoJob, where: RenderDestination) => {
    const set = where === 'local' ? setLocalJobs : setRemoteJobs;
    set(previous => [...tag([job], where), ...previous.filter(item => item.id !== job.id)]);
  };
  const start = async (input: VideoRequest) => {
    const where = current.current;
    setError(''); setSubmitting(true);
    try {
      if (where === 'remote') {
        if (!connection.available) throw new Error(connection.message);
        const id = await remote.submitCloudVideo(input);
        void remote.cloudVideo(id).then(job => update(job, where)).catch(() => undefined);
        return id;
      }
      const job = await createVideo(input); update(job, where); return job.id;
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to submit the render.'); }
    finally { setSubmitting(false); }
  };
  const jobs = [...localJobs, ...remoteJobs];
  const action = async (id: string, kind: 'cancel' | 'retry') => {
    const job = jobs.find(item => item.id === id);
    if (!job?.destination) { setError('The render destination is unknown. Refresh its history.'); return; }
    try {
      if (job.destination === 'local' && kind === 'retry') throw new Error('Open the local composition to render it again.');
      update(await (job.destination === 'remote' ? remote.cloudAction(id, kind) : cancelVideo(id)), job.destination);
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to update the render.'); }
  };
  const loadMore = async () => {
    if (!nextCursor) return;
    try { const page = await remote.cloudVideos(nextCursor); setRemoteJobs(previous => [...previous, ...tag(page.jobs, 'remote').filter(job => !previous.some(item => item.id === job.id))]); setNextCursor(page.nextCursor); }
    catch { setError('Unable to load older production videos.'); }
  };
  const edit = async (id: string, title?: string) => {
    if (!remoteJobs.some(job => job.id === id)) { setError('Local export history cannot be edited.'); return; }
    try {
      const job = await apiRequest<VideoJob>(`${PREFIX}${API}/render-jobs/${encodeURIComponent(id)}`, title === undefined ? { method: 'DELETE' } : { ...jsonPost({ title }), method: 'PATCH' });
      if (title === undefined) setRemoteJobs(previous => previous.filter(item => item.id !== id)); else update(job, 'remote');
    } catch { setError('Unable to update the production video.'); }
  };
  const commerce = destination === 'remote' && baseCommerce ? { ...baseCommerce, status: connection.connected ? 'ready' as const : 'signed-out' as const,
    entitlement: connection.entitlements ?? null, error: connection.message } : baseCommerce;
  return { active, destination, setDestination, connection, commerce, available: destination === 'local' ? localAvailable : connection.available,
    mode: destination === 'local' ? 'local' as const : 'cloud' as const, jobs: filter ? jobs.filter(job => filter === 'revoked' ? ['revoked', 'cancelled'].includes(job.state) : job.state === filter) : jobs, error, submitting, start, cancel: (id: string) => action(id, 'cancel'),
    retry: (id: string) => action(id, 'retry'), nextCursor, loadMore, edit,
    busy: submitting || (destination === 'local' && localJobs.some(job => job.state === 'processing')) };
}
